import { createContext, useState, useContext, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useCameraAllocator } from "../hooks/useCameraAllocator";
import { useMicAllocator } from "../hooks/useMicAllocator";
import { socket } from "../components/mediasoup/socket";

/* ---------------- Types ---------------- */

interface JudgeCameraList {
  userId: string;
  label: string;
  deviceId: string;
}

interface MeetingContextType {
  causeListShow: boolean;
  setCauseListShow: (show: boolean) => void;

  viewerShow: boolean;
  setViewerShow: (show: boolean) => void;

  recording: string;
  setRecording: (show: string) => void;

  userData: any;
  setUserData: (data: any) => void;

  pathname: string;

  setAdvocateCases: (data: any) => void;

  selectedCase: any;
  setSelectedCase: (data: any) => void;

  /* 🔥 Camera allocation (NEW) */
  cameraAssignments: Record<string, string>;
  assignCamera: (userId: string, deviceId: string) => void;

  /* (Optional legacy – can be removed later) */
  judgeCameraSelected: JudgeCameraList;
  setJudgeCameraSelected: (data: JudgeCameraList) => void;

  micAssignments: Record<string, string>;
  assignMic: (userId: string, deviceId: string) => void;

  // test
  test: any;
  setTest: (data: any) => void;
}

export const MeetingContext = createContext<MeetingContextType | null>(null);

/* ---------------- Provider ---------------- */

export const MeetingProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const location = useLocation();
  const pathname = location.pathname;

  /* ---------------- UI State ---------------- */

  const [causeListShow, setCauseListShow] = useState(true);
  const [viewerShow, setViewerShow] = useState<boolean>(false);
  const [recording, setRecording] = useState("");
  const [userData, setUserData] = useState<any>(null);
  const [selectedCase, setSelectedCase] = useState<any>(null);

  const [test, setTest] = useState<any>(null);

  const [advocateCases, setAdvocateCases] = useState<any>({
    socket: null,
    cases: [],
  });

  /* ---------------- Camera Allocation ---------------- */

  /**
   * IMPORTANT:
   * Participant IDs must be STABLE.
   * JudgeMeeting will create the same list.
   */
  const participantIds = ["Judge", "Prosecutor", "Defendant"];

  /**
   * Initial device IDs are unknown at context level.
   * Allocator will fill dynamically when assignCamera is called.
   */
  const { cameraAssignments, assignCamera } = useCameraAllocator({
    participantIds,
    initialDeviceIds: [],
  });

  /* ---------------- Mic Allocation ---------------- */

  const { micAssignments, assignMic } = useMicAllocator({
    participantIds,
    initialDeviceIds: [],
  });

  /* ---------------- Legacy (Safe to keep for now) ---------------- */

  const [judgeCameraSelected, setJudgeCameraSelected] =
    useState<JudgeCameraList>({
      userId: "",
      label: "",
      deviceId: "",
    });

  /* ---------------- Cleanup Judge Socket ---------------- */

  useEffect(() => {
    if (pathname === "/judge-meeting") return;
    if (userData?.role !== "Judge") return;
    if (!selectedCase) return;

    socket?.emit("end-meeting", {
      roomId: userData?.roomId,
      roomNo: userData?.roomNo,
      actionBy: userData?._id,
      cnr: selectedCase?.cnr,
    });
  }, [socket, pathname, userData, selectedCase]);

  /* ---------------- Advocate Leave Cleanup ---------------- */

  useEffect(() => {
    if (pathname === "/advocate-lobby" || pathname === "/meeting") return;
    if (!userData) return;

    advocateCases?.cases?.forEach((caseItem: any) => {
      advocateCases?.socket?.emit("leave-meeting", {
        meetingId: `${caseItem.courtRoom}_${caseItem.courtRoomId}`,
        roomNo: caseItem.courtRoom,
        roomId: caseItem.courtRoomId,
        userId: userData._id,
        role: userData.role,
        cnr: caseItem.cnr,
        reason: "USER_LEFT",
      });
    });
  }, [advocateCases, pathname, userData]);

  /* ---------------- Provider ---------------- */

  const contextValue = useMemo(
    () => ({
      causeListShow,
      setCauseListShow,
      viewerShow,
      setViewerShow,
      recording,
      setRecording,
      userData,
      setUserData,
      pathname,
      setAdvocateCases,
      selectedCase,
      setSelectedCase,

      /* 🔥 Camera allocator */
      cameraAssignments,
      assignCamera,

      /* legacy */
      judgeCameraSelected,
      setJudgeCameraSelected,

      micAssignments,
      assignMic,

      test,
      setTest,
    }),
    [
      causeListShow,
      setCauseListShow,
      viewerShow,
      setViewerShow,
      recording,
      setRecording,
      userData,
      setUserData,
      pathname,
      setAdvocateCases,
      selectedCase,
      setSelectedCase,
      cameraAssignments,
      assignCamera,
      judgeCameraSelected,
      setJudgeCameraSelected,
      micAssignments,
      assignMic,

      test,
      setTest,
    ],
  );

  return (
    <MeetingContext.Provider value={contextValue}>
      {children}
    </MeetingContext.Provider>
  );
};

/* ---------------- Hook ---------------- */

export const useMeeting = () => {
  const ctx = useContext(MeetingContext);
  if (!ctx) {
    throw new Error("useMeeting must be used within MeetingProvider");
  }
  return ctx;
};
